import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Send, X, MessageCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import ChatSafetyActions from "./ChatSafetyActions";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  return new Date(iso).toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
}

/**
 * 1:1 DM 모달 — 게시물 작성자와의 대화방
 * Props:
 *  - open
 *  - onClose
 *  - userId (상대방 ID)
 *  - userName (상대방 이름)
 */
export default function DMModal({ open, onClose, userId, userName }) {
  const { user, token } = useAuth();
  const [thread, setThread] = useState({ messages: [], user: null });
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 128) + "px";
    }
  }, [text]);

  const fetchThread = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/thread/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setThread({ messages: data.messages || [], user: data.user });
      }
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    if (open) fetchThread();
  }, [open, fetchThread]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread.messages]);

  const send = async () => {
    if (!text.trim()) return;
    if (!user) { toast.error("로그인이 필요합니다"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ receiver_id: userId, content: text.trim() }),
      });
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { /* keep empty */ }
      if (!res.ok) throw new Error(data.detail || raw || "전송 실패");
      // optimistic update
      setThread((t) => ({
        ...t,
        messages: [
          ...t.messages,
          {
            id: data.id,
            sender_id: user.id,
            receiver_id: userId,
            content: text.trim(),
            created_at: data.created_at || new Date().toISOString(),
            is_read: false,
          },
        ],
      }));
      setText("");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] bg-stone-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={onClose}
          data-testid="dm-modal-backdrop"
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col h-[85vh] sm:h-[600px]"
            data-testid="dm-modal"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-100">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-burgundy-500 to-rose-500 text-white flex items-center justify-center font-bold">
                {(thread.user?.username || userName || "?").charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-stone-900 truncate">
                  {thread.user?.username || userName || "대화"}
                </div>
                <div className="text-xs text-stone-400">따뜻한 말 한마디로 시작해보세요</div>
              </div>
              <ChatSafetyActions otherId={userId} otherName={thread.user?.username || userName} size="sm" />
              <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center" aria-label="닫기">
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-[#F9F8F6] scrollbar-thin">
              {loading ? (
                <div className="py-10 text-center text-stone-400">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : thread.messages.length === 0 ? (
                <div className="py-12 text-center">
                  <MessageCircle className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                  <p className="text-sm text-stone-500">아직 대화가 없어요.</p>
                  <p className="text-xs text-stone-400 mt-1">먼저 인사를 건네보세요 🌿</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {thread.messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <li key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[78%] ${mine ? "bg-burgundy-50 text-burgundy-900 border border-burgundy-100 rounded-3xl rounded-br-md" : "bg-white text-stone-800 border border-stone-100 rounded-3xl rounded-bl-md"} px-4 py-3 shadow-sm`}>
                          <p className="text-[15px] leading-[1.65] whitespace-pre-wrap break-words">{m.content}</p>
                          <div className={`text-[10px] mt-1.5 ${mine ? "text-burgundy-400" : "text-stone-400"}`}>{relTime(m.created_at)}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-stone-100 px-4 py-3 flex gap-2 items-end bg-white">
              <textarea
                value={text}
                ref={inputRef}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }}
                placeholder="따뜻한 메시지를 적어주세요... (줄바꿈은 Enter)"
                rows={1}
                className="flex-1 px-5 py-3 rounded-3xl bg-stone-50 border border-stone-200 text-[15px] leading-[1.5] focus:outline-none focus:border-burgundy-300 focus:ring-2 focus:ring-burgundy-100 resize-none max-h-32 overflow-y-auto"
                data-testid="dm-input"
              />
              <button
                onClick={send}
                disabled={sending || !text.trim()}
                className="w-12 h-12 shrink-0 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white flex items-center justify-center disabled:opacity-50 transition-colors"
                data-testid="dm-send"
                aria-label="보내기"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
